import React, { useState } from 'react';
import { Button, Select, TextInput, Textarea } from '../../../components/ui';
import { getFieldsForEntity } from '../utils/entityFields';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import useSWR from 'swr';
import api from '../../../services/api';

interface Action {
  type: string;
  params: Record<string, any>;
}

interface ActionParam {
  key: string;
  label: string;
  type: string;
  options?: Array<{ value: string; label: string }>;
  apiEndpoint?: string;
  labelField?: string;
  valueField?: string;
}

interface ActionSelectorProps {
  actions: Action[];
  onChange: (actions: Action[]) => void;
  entityType?: string;
}

const actionTypes = [
  { value: 'update_field', label: 'Update Field', description: 'Update a field on the entity' },
  { value: 'assign_user', label: 'Assign User', description: 'Assign a user to the entity' },
  { value: 'send_email', label: 'Send Email', description: 'Send an email notification' },
  { value: 'create_task', label: 'Create Task', description: 'Create a new task' },
  { value: 'create_notification', label: 'Create Notification', description: 'Create a notification' },
  { value: 'update_status', label: 'Update Status', description: 'Update entity status' },
  { value: 'log_activity', label: 'Log Activity', description: 'Log an activity entry' },
  { value: 'link_email_to_client', label: 'Link Email to Client', description: 'Link the email to a client (Email entity only)' },
];

const ActionSelector: React.FC<ActionSelectorProps> = ({ actions = [], onChange, entityType = 'Task' }) => {
  const { colors } = useTheme();
  const availableFields = getFieldsForEntity(entityType);
  const addAction = () => {
    onChange([
      ...actions,
      {
        type: '',
        params: {},
      },
    ]);
  };

  const updateAction = (index: number, updates: Partial<Action>) => {
    const updated = [...actions];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const updateActionParam = (index: number, key: string, value: any) => {
    const updated = [...actions];
    updated[index] = {
      ...updated[index],
      params: {
        ...updated[index].params,
        [key]: value,
      },
    };
    onChange(updated);
  };

  const removeAction = (index: number) => {
    const updated = actions.filter((_, i) => i !== index);
    onChange(updated);
  };

  const getActionParams = (actionType: string): ActionParam[] => {
    switch (actionType) {
      case 'update_field':
        return [
          { key: 'field', label: 'Field Name', type: 'select', options: availableFields.map(f => ({ value: f.name, label: f.label })) },
          { key: 'value', label: 'Value', type: 'text' },
        ];
      case 'assign_user':
        return [
          { key: 'userId', label: 'User', type: 'foreignKey', apiEndpoint: '/users', labelField: 'userDisplayName', valueField: 'id' },
        ];
      case 'send_email':
        return [
          { key: 'to', label: 'To', type: 'text' },
          { key: 'subject', label: 'Subject', type: 'text' },
          { key: 'body', label: 'Body', type: 'textarea' },
        ];
      case 'create_task':
        return [
          { key: 'title', label: 'Task Title', type: 'text' },
          { key: 'description', label: 'Description', type: 'textarea' },
          { key: 'assignedTo', label: 'Assigned To', type: 'foreignKey', apiEndpoint: '/users', labelField: 'userDisplayName', valueField: 'id' },
          { key: 'clientId', label: 'Client', type: 'foreignKey', apiEndpoint: '/crm-clients', labelField: 'clientName', valueField: 'clientId' },
          { key: 'priority', label: 'Priority', type: 'text' },
          { key: 'status', label: 'Status', type: 'text' },
        ];
      case 'create_notification':
        return [
          { key: 'userId', label: 'User', type: 'foreignKey', apiEndpoint: '/users', labelField: 'userDisplayName', valueField: 'id' },
          { key: 'message', label: 'Message', type: 'text' },
        ];
      case 'update_status':
        return [
          { key: 'status', label: 'Status', type: 'text' },
        ];
      case 'log_activity':
        return [
          { key: 'message', label: 'Message', type: 'text' },
        ];
      case 'link_email_to_client':
        return [
          { key: 'clientId', label: 'Client', type: 'foreignKey', apiEndpoint: '/crm-clients', labelField: 'clientName', valueField: 'clientId' },
        ];
      default:
        return [];
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {actions.map((action, index) => {
          const params = getActionParams(action.type);
          return (
            <div key={index} className={cn('p-4 border rounded-lg', colors.surfaceHover, colors.border)}>
              <div className="flex items-start gap-2 mb-3">
                <Select
                  value={action.type}
                  onChange={(e) => updateAction(index, { type: e.target.value, params: {} })}
                  className="flex-1"
                  options={[
                    { value: '', label: 'Select Action Type' },
                    ...actionTypes
                      .filter((t) => (t.value === 'link_email_to_client' ? entityType === 'Email' : true))
                      .map((t) => ({ value: t.value, label: t.label })),
                  ]}
                />
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeAction(index)}
                >
                  Remove
                </Button>
              </div>

              {action.type && (
                <div className="space-y-2">
                  {params.map((param) => {
                    const selectedField = param.key === 'field' && 'options' in param && param.options
                      ? availableFields.find(f => f.name === action.params[param.key])
                      : null;
                    
                    return (
                      <div key={param.key}>
                        <label className={cn('block text-sm font-medium mb-1', colors.textSecondary)}>
                          {param.label}
                        </label>
                        {param.type === 'select' && 'options' in param && param.options ? (
                          <Select
                            value={String(action.params[param.key] || '')}
                            onChange={(e) => {
                              updateActionParam(index, param.key, e.target.value);
                              if (param.key === 'field') {
                                const field = availableFields.find(f => f.name === e.target.value);
                                if (field) {
                                  updateActionParam(index, 'value', field.type === 'boolean' ? false : field.type === 'number' ? 0 : '');
                                }
                              }
                            }}
                            options={[
                              { value: '', label: `Select ${param.label}` },
                              ...param.options.map((opt: { value: string; label: string }) => ({ value: String(opt.value), label: opt.label })),
                            ]}
                          />
                        ) : param.type === 'foreignKey' && 'apiEndpoint' in param ? (
                          // Foreign key field - show dropdown with related entities
                          <ForeignKeySelect
                            apiEndpoint={param.apiEndpoint || ''}
                            labelField={param.labelField || 'name'}
                            valueField={param.valueField || 'id'}
                            value={action.params[param.key]}
                            onChange={(value) => updateActionParam(index, param.key, Number(value) || 0)}
                          />
                        ) : param.type === 'textarea' ? (
                          <Textarea
                            value={String(action.params[param.key] || '')}
                            onChange={(e) => updateActionParam(index, param.key, e.target.value)}
                            placeholder={`Enter ${param.label.toLowerCase()}`}
                            rows={3}
                          />
                        ) : param.key === 'value' && selectedField ? (
                          selectedField.type === 'boolean' ? (
                            <Select
                              value={String(action.params[param.key] || 'false')}
                              onChange={(e) => updateActionParam(index, param.key, e.target.value === 'true')}
                              options={[
                                { value: 'true', label: 'True' },
                                { value: 'false', label: 'False' },
                              ]}
                            />
                          ) : selectedField.apiEndpoint && selectedField.type === 'number' ? (
                            // Foreign key field - show dropdown with related entities
                            <ForeignKeySelect
                              apiEndpoint={selectedField.apiEndpoint}
                              labelField={selectedField.labelField || 'name'}
                              valueField={selectedField.valueField || 'id'}
                              value={action.params[param.key]}
                              onChange={(value) => updateActionParam(index, param.key, Number(value) || 0)}
                            />
                          ) : selectedField.type === 'date' ? (
                            <TextInput
                              type="date"
                              value={action.params[param.key] ? new Date(action.params[param.key]).toISOString().split('T')[0] : ''}
                              onChange={(e) => updateActionParam(index, param.key, e.target.value)}
                            />
                          ) : selectedField.type === 'number' ? (
                            <TextInput
                              type="number"
                              value={String(action.params[param.key] || '')}
                              onChange={(e) => updateActionParam(index, param.key, Number(e.target.value) || 0)}
                            />
                          ) : selectedField.type === 'select' && selectedField.options ? (
                            <Select
                              value={String(action.params[param.key] || '')}
                              onChange={(e) => updateActionParam(index, param.key, e.target.value)}
                              options={[
                                { value: '', label: 'Select Value' },
                                ...selectedField.options.map((opt) => ({ value: String(opt.value), label: opt.label })),
                              ]}
                            />
                          ) : (
                            <TextInput
                              type={param.type}
                              value={String(action.params[param.key] || '')}
                              onChange={(e) => updateActionParam(index, param.key, e.target.value)}
                              placeholder={`Enter ${param.label.toLowerCase()}`}
                            />
                          )
                        ) : (
                          <TextInput
                            type={param.type}
                            value={String(action.params[param.key] || '')}
                            onChange={(e) =>
                              updateActionParam(
                                index,
                                param.key,
                                param.type === 'number' ? Number(e.target.value) : e.target.value
                              )
                            }
                            placeholder={`Enter ${param.label.toLowerCase()}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Button variant="outline" onClick={addAction}>
        + Add Action
      </Button>
    </div>
  );
};

// Component to handle fetching and displaying foreign key values in actions
interface ForeignKeySelectProps {
  apiEndpoint: string;
  labelField: string;
  valueField: string;
  value: any;
  onChange: (value: any) => void;
}

const ForeignKeySelect: React.FC<ForeignKeySelectProps> = ({
  apiEndpoint,
  labelField,
  valueField,
  value,
  onChange,
}) => {
  const { data: relatedEntities, isLoading } = useSWR<any[]>(
    apiEndpoint,
    async (url: string) => {
      const response = await api.get(url);
      return Array.isArray(response.data) ? response.data : [];
    }
  );

  const options = React.useMemo(() => {
    if (!relatedEntities) return [];
    return relatedEntities.map((entity: any) => ({
      value: entity[valueField],
      label: entity[labelField] || String(entity[valueField]),
    }));
  }, [relatedEntities, labelField, valueField]);

  return (
    <Select
      value={String(value || '')}
      onChange={(e) => onChange(e.target.value)}
      disabled={isLoading}
      options={[
        { value: '', label: isLoading ? 'Loading...' : 'Select Value' },
        ...options.map((opt) => ({ value: String(opt.value), label: opt.label })),
      ]}
    />
  );
};

export default ActionSelector;
